/**
 * RelationshipGraph — entity-to-entity relationship visualiser.
 *
 * Reusable across ALL explorers: pharmaceutical companies, regulators,
 * surveillance systems, ingredients, diseases, future medicines etc.
 *
 * Renders a compact grouped list view by default. The list view is
 * intentionally chosen over a force-directed SVG graph because:
 *   (a) it's accessible / screen-reader friendly,
 *   (b) it's scannable at a glance,
 *   (c) it scales gracefully when an entity has 50+ relationships,
 *   (d) it gets indexed by search engines (the SEO win the spec demands).
 *
 * Props
 *   focusEntity  { id, name } — the entity this graph is centred on.
 *   relationships  Array of relationship docs from /api/admin/entity-relationships
 *                  or /api/entity-relationships?entity_id=...
 *   entitiesById   Map<id, { name, slug, entity_type }> for resolution.
 *   parentRef      Research-trail parent for SaveButton.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Network } from "lucide-react";
import SaveButton from "./SaveButton";

const TYPE_LABELS = {
    parent_of: "Parent of",
    subsidiary_of: "Subsidiary of",
    acquired: "Acquired",
    divested: "Divested",
    merged_with: "Merged with",
    partnered_with: "Partnered with",
    joint_venture_with: "Joint venture with",
    licensed_from: "Licensed from",
    licensed_to: "Licensed to",
    manufactures: "Manufactures",
    regulates: "Regulates",
    approved_by: "Approved by",
    monitored_by: "Monitored by",
    targets_disease: "Targets disease",
    contains_ingredient: "Contains",
    studied_in: "Studied in",
    funded_by: "Funded by",
    other: "Related",
};

const GROUP_FOR = {
    parent_of: "Corporate",
    subsidiary_of: "Corporate",
    acquired: "Corporate",
    divested: "Corporate",
    merged_with: "Corporate",
    partnered_with: "Corporate",
    joint_venture_with: "Corporate",
    licensed_from: "Corporate",
    licensed_to: "Corporate",
    manufactures: "Domain",
    regulates: "Domain",
    approved_by: "Domain",
    monitored_by: "Domain",
    targets_disease: "Domain",
    contains_ingredient: "Domain",
    studied_in: "Domain",
    funded_by: "Domain",
    other: "Other",
};

function EntityChip({ id, entitiesById, fallback }) {
    const e = entitiesById?.[id];
    if (!e) return <span className="text-[#94A3B8]">{fallback || id}</span>;
    return (
        <Link
            to={`/entity/${e.slug}`}
            className="inline-flex items-center gap-1.5 text-white hover:text-[#56CCF2] transition-colors"
        >
            {e.name}
            <span className="text-[9px] uppercase tracking-wider text-[#64748B]">{(e.entity_type || "").replace("_", " ")}</span>
        </Link>
    );
}

export default function RelationshipGraph({ focusEntity, relationships = [], entitiesById = {}, parentRef = null, className = "" }) {
    const grouped = useMemo(() => {
        const out = {};
        for (const r of relationships) {
            const group = GROUP_FOR[r.relationship_type] || "Other";
            (out[group] = out[group] || []).push(r);
        }
        return out;
    }, [relationships]);

    if (!relationships.length) {
        return (
            <div className={`bg-[#0B1220] border border-[#243044] rounded-xl p-6 ${className}`} data-testid="relationship-graph-empty">
                <div className="text-[12px] text-[#64748B] italic">No mapped relationships yet for this entity.</div>
            </div>
        );
    }

    return (
        <section
            className={`bg-[#0B1220] border border-[#243044] rounded-xl p-6 ${className}`}
            data-testid="relationship-graph"
        >
            <div className="flex items-center gap-2 mb-5">
                <Network className="w-4 h-4 text-[#56CCF2]" />
                <h3 className="text-sm font-semibold text-white tracking-tight">Relationships</h3>
                <span className="text-[10px] text-[#64748B] ml-2">{relationships.length} mapped</span>
            </div>
            <div className="space-y-5">
                {Object.entries(grouped).map(([group, rels]) => (
                    <div key={group}>
                        <h4 className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-2">{group}</h4>
                        <div className="space-y-1.5">
                            {rels.map((r) => {
                                const fromIsFocus = focusEntity && r.from_entity_id === focusEntity.id;
                                const otherId = fromIsFocus ? r.to_entity_id : r.from_entity_id;
                                const verb = TYPE_LABELS[r.relationship_type] || r.relationship_type;
                                return (
                                    <div
                                        key={r.id}
                                        className="flex items-center gap-2 text-sm bg-[#111827] border border-[#243044] rounded-md px-3 py-2 hover:border-[#56CCF2]/40 transition-colors"
                                        data-testid={`relationship-row-${r.id}`}
                                    >
                                        <span className="text-[11px] uppercase tracking-wider text-[#94A3B8] min-w-[120px]">
                                            {fromIsFocus ? verb : `← ${verb}`}
                                        </span>
                                        <ArrowRight className="w-3 h-3 text-[#64748B]" />
                                        <EntityChip id={otherId} entitiesById={entitiesById} />
                                        {r.started_at ? (
                                            <span className="ml-auto text-[11px] text-[#64748B] font-mono">{r.started_at}</span>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            {focusEntity ? (
                <div className="mt-5 pt-4 border-t border-[#243044]">
                    <SaveButton
                        kind="entity"
                        refSlug={focusEntity.slug || focusEntity.id}
                        refTitle={focusEntity.name}
                        parentRef={parentRef}
                        variant="pill"
                        className=""
                    />
                </div>
            ) : null}
        </section>
    );
}
